/* eslint-disable no-console */
/* global gtag */
/**
 * @module Analytics
 */
const { toType } = require('./toType');

/**
 * Convenience function to fire off a gtag config call. This allows you to add
 * additional configuration information to targets. This is typically a
 * product-specific configuration for a product, but you only need to configure
 * this once if you're using both Google Ads and Google Analytics.
 *
 * @param {object} params - The function parameters object.
 * @param {object} [params.parameters] - The parameters object associated with the gtag('config') call (Default: {}).
 * @param {string} params.tagId - The identifier that uniquely identifies the target, such as a Google Analytics property (e.g. 'G-8008675309').
 *
 * @throws {Error} Throws an error if invalid type is supplied.
 *
 * @see {@link https://developers.google.com/tag-platform/gtagjs/reference#config}.
 */
function callGtagConfig({ parameters, tagId }) {
  if (!tagId || toType(tagId) !== 'string') {
    throw new Error(`A valid tagId (string) must be specified.`);
  }

  try {
    gtag('config', tagId, parameters);
  } catch (error) {
    // Silent failure is acceptable.
    console.warn('gtag is not available in this environment.', {
      parameters,
      tagId,
    });
  }
}

/**
 * Convenience function to fire off a gtag event call to send event data.
 *
 * @param {object} params - The function parameters object.
 * @param {object} [params.parameters] - The parameters object associated with the gtag('event') call (Default: {}).
 * @param {string} params.type - The event type (e.g. 'Click').
 *
 * @throws {Error} Throws an error if invalid type is supplied.
 *
 * @see {@link https://developers.google.com/tag-platform/gtagjs/reference#event}.
 */
function callGtagEvent({ parameters, type }) {
  if (!type || toType(type) !== 'string') {
    throw new Error(`A valid event type (string) must be specified.`);
  }

  try {
    gtag('event', type, parameters);
  } catch (error) {
    // Silent failure is acceptable.
    console.warn('gtag is not available in this environment.', {
      parameters,
      type,
    });
  }
}

/**
 * Convenience function to fire off a Segment Identify call, which lets you tie
 * a user to their actions and record traits about them. It includes a unique
 * User ID and any optional traits you know about the user, like their email,
 * name, and more.
 *
 * @param {object} params - The function parameters object.
 * @param {object} params.traits - The traits object associated with the Identify call.
 * @param {string} params.userId - The user id associated with the Identify call.
 *
 * @throws {Error} Throws an error if invalid userId is supplied.
 *
 * @see {@link https://segment.com/docs/connections/spec/identify/}.
 */
function callSegmentIdentify({ traits, userId }) {
  if (!userId) {
    throw new Error(`A valid userId (string) must be specified.`);
  }

  try {
    window.analytics.identify(userId, traits);
  } catch (error) {
    // Silent failure is acceptable.
    console.warn('Segment Identify not available in this environment.', {
      traits,
      userId,
    });
  }
}

/**
 * Convenience function to fire off a Segment Page call, which lets you record
 * whenever a user sees a page of your website, along with any optional
 * properties about the page. This is one of the first steps to getting started
 * with Segment.
 *
 * @param {object} params - The function parameters object.
 * @param {string} params.category - The category for the Page call.
 * @param {string} params.name - The name for the Page call.
 * @param {object} params.properties - The properties object associated with the Page call.
 *
 * @throws {Error} Throws an error if invalid name or properties are supplied.
 *
 * @see {@link https://segment.com/docs/connections/spec/page/}.
 */
function callSegmentPage({ category, name, properties }) {
  if (!name || !properties) {
    throw new Error(
      `A valid name (string) and properties (object) must be specified.`,
    );
  }

  try {
    window.analytics.page({
      category,
      name,
      properties,
    });
  } catch (error) {
    // Silent failure is acceptable.
    console.warn('Segment Page not available in this environment.', {
      category,
      name,
      properties,
    });
  }
}

/**
 * Convenience function to fire off a Segment Track call, which records an
 * action performed by a user, along with properties that describe the action.
 *
 * @param {object} params - The function parameters object.
 * @param {string} params.event - The event for the Track call.
 * @param {object} params.properties - The properties object associated with the Track call.
 *
 * @throws {Error} Throws an error if invalid event or properties are supplied.
 *
 * @see {@link https://segment.com/docs/connections/spec/track/}.
 */
function callSegmentTrack({ event, properties }) {
  if (!event || !properties || !Object.keys(properties).length) {
    throw new Error(
      `A valid event (string) and properties (object) must be specified.`,
    );
  }

  try {
    window.analytics.track({
      event,
      properties,
    });
  } catch (error) {
    // Silent failure is acceptable.
    console.warn('Segment Track not available in this environment.', {
      event,
      properties,
    });
  }
}

module.exports = {
  callGtagConfig,
  callGtagEvent,
  callSegmentIdentify,
  callSegmentPage,
  callSegmentTrack,
};
